import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller, FieldValues } from "react-hook-form";
import SubHeader from "../../layouts/subheader/SubHeader";
import { useNavigate } from "react-router-dom";
// Import timeline components and icons
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import config from '../../env.json';
import { getBaseTemplates } from "../../apis/templateApi/baseTemplateView";

// Template interface definition
interface Template {
  id: string;
  template: string;
  config_cd: string;
  config_url: string;
  config_desc: string;
  zone: string;
  services: string[];
}

const CreateTenant = () => {
  const { handleSubmit, control, watch } = useForm();
  const navigate = useNavigate();

  // Watch for primaryColor changes.
  const primaryColorValue = watch("primaryColor", "#000000");

  // State for modal visibility and status updates.
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  // State for templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch templates when component mounts
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const templatesData = await getBaseTemplates();
        setTemplates(templatesData);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch templates:", err);
        setError("Failed to load templates. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Clean up the EventSource when component unmounts or modal is closed.
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const onSubmit = async (data: FieldValues) => {
    let body = {
      Client_Nm: data.tenantName,
      Client_Dsc: data.tenantName,
      Client_Size: data.tenantSize,
      Env_Nm: "dev",
      Env_Dsc: data.tenantName + "dev2",
      // services: data.services,
      domain_name: data.domainName,
      indexColor: data.primaryColor,
      configCode: data.template
    };

    // Open the modal and clear any previous messages.
    setOpenStatusModal(true);
    setStatusMessages([]);

    const es = new EventSource(`${config.REACT_APP_ENV_API_BASE}/tenantProvisioning/status/stream`);
    setEventSource(es);

    es.onmessage = (e) => {
      setStatusMessages((prev) => [...prev, e.data]);

      if (e.data.includes("completed") || e.data.includes("Error occurred")) {
        es.close();
      }
    };

    es.onerror = (err) => {
      console.error("EventSource failed:", err);
      setStatusMessages((prev) => [
        ...prev,
        "Error connecting to status stream.",
      ]);
      es.close();
    };

    // Trigger the create API call.
    try {
      const response = await axios.post(`${config.REACT_APP_ENV_API_BASE}/tenantProvisioning`, body);
      console.log("Create API response:", response.data);
    } catch (error: any) {
      console.error("Error calling create API:", error);
      // setStatusMessages((prev) => [...prev, "API call failed"]);
      // es.close();
    }
  };

  // For the form menu.
  const menuItems = [
    { label: "Create Client", onClick: () => navigate("create") },
  ];

  // Helper function to determine icon and color for timeline items
  const getTimelineItemProps = (message: string) => {
    if (message.includes("completed")) {
      return {
        icon: <CheckCircleOutlineIcon />,
        color: "success",
        dotVariant: "outlined"
      };
    } else if (message.includes("Error")) {
      return {
        icon: <ErrorOutlineIcon />,
        color: "error",
        dotVariant: "outlined"
      };
    } else {
      return {
        icon: <PendingOutlinedIcon />,
        color: "primary",
        dotVariant: "outlined"
      };
    }
  };

  // Generate timestamp for timeline items
  const getTimeString = () => {
    const now = new Date();
    return now.toLocaleTimeString();
  };

  return (
    <div className="tab-page-layout-1">
      <SubHeader
        title="Create Tenant"
        dropdownLabel="Options"
        menuItems={menuItems}
      />

      <div className="tab-page-layout-1-body">
        <Box p={4}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4}>
              {/* First Column */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  style={{
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Template Information
                  </Typography>

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="template-select-label">
                      Select Template
                    </InputLabel>
                    <Controller
                      name="template"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="template-select-label"
                          label="Select Template"
                          disabled={loading}
                        >
                          {loading ? (
                            <MenuItem value="" disabled>
                              <Box display="flex" alignItems="center">
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Loading templates...
                              </Box>
                            </MenuItem>
                          ) : error ? (
                            <MenuItem value="" disabled>
                              Error loading templates
                            </MenuItem>
                          ) : templates.length === 0 ? (
                            <MenuItem value="" disabled>
                              No templates available
                            </MenuItem>
                          ) : (
                            templates.map((template) => (
                              <MenuItem key={template.id} value={template.config_cd}>
                                {template.config_cd}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      )}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="zone-select-label">Select Zone</InputLabel>
                    <Controller
                      name="zone"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="zone-select-label"
                          label="Select Zone"
                        >
                          <MenuItem value="zone1">US Central</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Paper>
              </Grid>

              {/* Second Column */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  style={{
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Tenant Information
                  </Typography>

                  <Controller
                    name="tenantName"
                    control={control}
                    defaultValue=""
                    rules={{
                      maxLength: {
                        value: 15,
                        message: 'Tenant Name cannot exceed 15 characters',
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        margin="normal"
                        id="tenant-name"
                        label="Tenant Name"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          maxLength: 15, // Optional: prevents typing beyond 15 chars
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="tenantSize"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        margin="normal"
                        id="tenant-size"
                        label="Tenant Size"
                        variant="outlined"
                        type="number"
                      />
                    )}
                  />
                  <Controller
                    name="domainName"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value.endsWith('/')) {
                          return 'Domain should not have a trailing "/"';
                        }
                        return true;
                      },
                    }}
                    render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        margin="normal"
                        id="domain-name"
                        label="Domain Name"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>https://</span>,
                        }}
                        value={value?.replace(/^https?:\/\//, '') || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          const fullUrl = `https://${inputValue}`;
                          onChange(fullUrl);
                        }}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Box display="flex" alignItems="center">
                    {/* New Primary Color field with reduced width */}
                    <Controller
                      name="primaryColor"
                      control={control}
                      defaultValue="#000000"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          margin="normal"
                          id="primary-color"
                          label="Primary Color"
                          type="color"
                          InputLabelProps={{ shrink: true }}
                          style={{ width: "200px", marginRight: "16px" }}
                        />
                      )}
                    />

                    {/* Display the selected hex value */}
                    <Typography
                      variant="body1"
                      fontWeight={"600"}
                      style={{ marginTop: "8px" }}
                    >
                      Selected Color: {primaryColorValue}
                    </Typography>
                  </Box>

                  {/* Controlled Services Checkboxes */}
                  {/* <Controller
                    name="services"
                    control={control}
                    defaultValue={[]}
                    render={({ field: { value, onChange } }) => (
                      <FormGroup>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          style={{ marginTop: "16px" }}
                        >
                          Select Services
                        </Typography>
                        {[
                          { label: "Message", value: "message" },
                          { label: "Call", value: "call" },
                          { label: "Calendar", value: "calendar" },
                          { label: "Video", value: "video" },
                        ].map((service) => (
                          <FormControlLabel
                            key={service.value}
                            control={ 
                              <Checkbox
                                checked={value.includes(service.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    onChange([...value, service.value]);
                                  } else {
                                    onChange(
                                      value.filter(
                                        (v: string) => v !== service.value
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={service.label}
                          />
                        ))}
                      </FormGroup>
                    )}
                  /> */}
                </Paper>
              </Grid>
            </Grid>

            <Box mt={4} textAlign="center">
              <Button variant="contained" color="primary" type="submit">
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </div>

      {/* Modern Status Modal with Timeline */}
      <Dialog
        open={openStatusModal}
        onClose={() => {
          if (eventSource) eventSource.close();
          setOpenStatusModal(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h6" component="div" fontWeight="500">
            Deployment Status
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            height: "400px",
            overflowY: "auto",
            p: 0 // Remove default padding for cleaner timeline
          }}
        >
          <Timeline position="right" sx={{ p: 0, m: 0 }}>
            {statusMessages.map((msg, idx) => {
              const { icon, color, dotVariant } = getTimelineItemProps(msg);
              return (
                <TimelineItem key={idx}>
                  <TimelineOppositeContent
                    color="text.secondary"
                    sx={{ flex: 0.2, minWidth: "80px" }}
                  >
                    {getTimeString()}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={color as any} variant={dotVariant as any}>
                      {icon}
                    </TimelineDot>
                    {idx < statusMessages.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 2, px: 2 }}>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        color: msg.includes('Error') ? 'error.main' : 'text.primary',
                        fontWeight: msg.includes('completed') ? 500 : 400
                      }}
                    >
                      {msg}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              if (eventSource) eventSource.close();
              setOpenStatusModal(false);
            }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateTenant;